/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/**
 * Inventory Conditions Hook for ArendaPro+
 * 
 * Provides CRUD operations for inventory conditions using the new domain-specific table.
 * Replaces the generic tags system for inventory condition management.
 * 
 * Following STYLEGUIDE.md patterns:
 * - Uses supabaseWorker for data operations
 * - Proper error handling and loading states
 * - React Query for caching and synchronization
 * - TypeScript strict typing
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabaseWorker } from '@/integrations/supabase/workerClient';
import { useAuth } from '@/context/AuthContext';
import { 
  InventoryCondition, 
  InventoryConditionDb, 
  InventoryConditionUpdate,
  DomainStatusOption,
  DomainStatusFilters,
  DEFAULT_INVENTORY_CONDITIONS
} from '@/types/domain-statuses';
import {
  inventoryConditionDbToClient,
  inventoryConditionClientToDbInsert,
  inventoryConditionUpdateToDb,
  statusesToOptions,
  validateStatusName,
  validateColorCode,
  sanitizeStatusInput
} from '@/lib/mappers/domainStatusMappers';
import { toast } from 'sonner';

// =============================================================================
// QUERY KEYS
// =============================================================================

const INVENTORY_CONDITIONS_QUERY_KEY = 'inventoryConditions';

export const inventoryConditionsKeys = {
  all: [INVENTORY_CONDITIONS_QUERY_KEY] as const,
  lists: () => [...inventoryConditionsKeys.all, 'list'] as const,
  list: (filters: DomainStatusFilters) => [...inventoryConditionsKeys.lists(), filters] as const,
  details: () => [...inventoryConditionsKeys.all, 'detail'] as const,
  detail: (id: string) => [...inventoryConditionsKeys.details(), id] as const,
};

// =============================================================================
// MAIN HOOK
// =============================================================================

export type UseInventoryConditionsOptions = {
  filters?: DomainStatusFilters;
  enabled?: boolean;
}

export function useInventoryConditions(options: UseInventoryConditionsOptions = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { filters = {}, enabled = true } = options;

  // =============================================================================
  // QUERIES
  // =============================================================================

  const {
    data: inventoryConditions = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: inventoryConditionsKeys.list(filters),
    queryFn: async (): Promise<InventoryCondition[]> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      let query = supabaseWorker
        .from('inventory_conditions')
        .select('*')
        .eq('user_id', user.id)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (filters.search) {
        query = query.ilike('name', `%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching inventory conditions:', error);
        const errMsg = (error as { message?: string } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to fetch inventory conditions: ${errMsg}`);
      }

      const conditions = (data as InventoryConditionDb[]).map(inventoryConditionDbToClient);
      
      // FALLBACK: Если условий нет, возвращаем дефолтные для предотвращения пустых селектов
      if (conditions.length === 0) {
        console.warn('[useInventoryConditions] No conditions found, using fallback defaults');
        return DEFAULT_INVENTORY_CONDITIONS.map((name, index) => ({
          id: `fallback-${index}`,
          name,
          color: '#6b7280',
          description: `Состояние инвентаря: ${name}`,
          sortOrder: index + 1,
          userId: user.id,
          createdAt: new Date(),
          updatedAt: new Date(),
        }));
      }
      
      return conditions;
    },
    enabled: enabled && !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // =============================================================================
  // MUTATIONS
  // =============================================================================

  const createMutation = useMutation({
    mutationFn: async (newCondition: Omit<InventoryCondition, 'id' | 'createdAt' | 'updatedAt' | 'userId'>): Promise<InventoryCondition> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Validate input
      const sanitized = sanitizeStatusInput(newCondition);
      
      if (!validateStatusName(sanitized.name || '')) {
        throw new Error('Название состояния инвентаря должно содержать от 1 до 100 символов');
      }

      if (sanitized.color && !validateColorCode(sanitized.color)) {
        throw new Error('Некорректный формат цвета. Используйте HEX формат (например, #10b981)');
      }

      // Check for duplicates
      const existingCondition = inventoryConditions.find(
        condition => condition.name.toLowerCase() === sanitized.name?.toLowerCase()
      );
      
      if (existingCondition) {
        throw new Error('Состояние инвентаря с таким названием уже существует');
      }

      const insertData = inventoryConditionClientToDbInsert({
        ...sanitized,
        userId: user.id,
        color: sanitized.color || '#8b5cf6', // default color
      } as Omit<InventoryCondition, 'id' | 'createdAt' | 'updatedAt'> & { userId: string });

      const { data, error } = await supabaseWorker
        .from('inventory_conditions')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Error creating inventory condition:', error);
        const errMsg = (error as { message?: string } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to create inventory condition: ${errMsg}`);
      }

      return inventoryConditionDbToClient(data as InventoryConditionDb);
    },
    onSuccess: (newCondition) => {
      // Invalidate and refetch inventory conditions
      queryClient.invalidateQueries({ queryKey: inventoryConditionsKeys.all });
      toast.success(`Состояние инвентаря "${newCondition.name}" создано`);
    },
    onError: (error: Error) => {
      console.error('Error creating inventory condition:', error);
      toast.error(error.message || 'Ошибка при создании состояния инвентаря');
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: InventoryConditionUpdate }): Promise<InventoryCondition> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Validate input
      const sanitized = sanitizeStatusInput(updates);

      if (sanitized.name && !validateStatusName(sanitized.name)) {
        throw new Error('Название состояния инвентаря должно содержать от 1 до 100 символов');
      }

      if (sanitized.color && !validateColorCode(sanitized.color)) {
        throw new Error('Некорректный формат цвета. Используйте HEX формат (например, #10b981)');
      }

      // Check for duplicates (excluding current condition)
      if (sanitized.name) {
        const existingCondition = inventoryConditions.find(
          condition => condition.id !== id && condition.name.toLowerCase() === sanitized.name?.toLowerCase()
        );
        
        if (existingCondition) {
          throw new Error('Состояние инвентаря с таким названием уже существует');
        }
      }

      const updateData = inventoryConditionUpdateToDb(sanitized);

      const { data, error } = await supabaseWorker
        .from('inventory_conditions')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) {
        console.error('Error updating inventory condition:', error);
        const errMsg = (error as { message?: string } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to update inventory condition: ${errMsg}`);
      }

      if (!data) {
        throw new Error('Inventory condition not found or access denied');
      }

      return inventoryConditionDbToClient(data as InventoryConditionDb);
    },
    onSuccess: (updatedCondition) => {
      // Invalidate and refetch inventory conditions
      queryClient.invalidateQueries({ queryKey: inventoryConditionsKeys.all });
      toast.success(`Состояние инвентаря "${updatedCondition.name}" обновлено`);
    },
    onError: (error: Error) => {
      console.error('Error updating inventory condition:', error);
      toast.error(error.message || 'Ошибка при обновлении состояния инвентаря');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const { error } = await supabaseWorker
        .from('inventory_conditions')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting inventory condition:', error);
        const errMsg = (error as { message?: string } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to delete inventory condition: ${errMsg}`);
      }
    },
    onSuccess: (_, deletedId) => {
      // Find the deleted condition name for toast
      const deletedCondition = inventoryConditions.find(condition => condition.id === deletedId);
      
      // Invalidate and refetch inventory conditions
      queryClient.invalidateQueries({ queryKey: inventoryConditionsKeys.all });
      toast.success(`Состояние инвентаря "${deletedCondition?.name || ''}" удалено`);
    },
    onError: (error: Error) => {
      console.error('Error deleting inventory condition:', error);
      toast.error(error.message || 'Ошибка при удалении состояния инвентаря');
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (items: Array<{ id: string; sortOrder: number }>): Promise<void> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Update each item's sort_order directly via Supabase
      const updates = items.map(item =>
        supabaseWorker
          .from('inventory_conditions')
          .update({ sort_order: item.sortOrder })
          .eq('id', item.id)
          .eq('user_id', user.id)
      );

      const results = await Promise.all(updates);
      
      // Check for errors
      const errors = results.filter(r => r.error);
      if (errors.length > 0) {
        console.error('Error reordering inventory conditions:', errors);
        throw new Error('Failed to reorder some items');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: inventoryConditionsKeys.all });
      toast.success('Порядок обновлен');
    },
    onError: (error: Error) => {
      console.error('Error reordering inventory conditions:', error);
      toast.error(error.message || 'Ошибка при изменении порядка');
    },
  });

  // =============================================================================
  // COMPUTED VALUES
  // =============================================================================

  // КРИТИЧНО: Для инвентаря используем name как value, так как БД хранит строку, а не FK
  const conditionOptions: DomainStatusOption[] = statusesToOptions(inventoryConditions, true);

  const goodCondition = inventoryConditions.find(condition => 
    condition.name.toLowerCase() === 'хорошее' || 
    condition.name.toLowerCase() === 'good'
  );

  const fairCondition = inventoryConditions.find(condition => 
    condition.name.toLowerCase() === 'норм' || 
    condition.name.toLowerCase() === 'fair'
  );

  const poorCondition = inventoryConditions.find(condition => 
    condition.name.toLowerCase() === 'плохое' || 
    condition.name.toLowerCase() === 'poor'
  );

  // =============================================================================
  // RETURN INTERFACE
  // =============================================================================

  return {
    // Data
    inventoryConditions,
    options: conditionOptions,
    goodCondition,
    fairCondition,
    poorCondition,
    
    // State
    isLoading,
    error,
    
    // Actions
    refetch,
    create: createMutation.mutate,
    update: updateMutation.mutate,
    delete: deleteMutation.mutate,
    reorder: reorderMutation.mutate,
    
    // Mutation states
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isReordering: reorderMutation.isPending,
    
    // Mutation errors
    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    reorderError: reorderMutation.error,
  };
}

// =============================================================================
// UTILITY HOOKS
// =============================================================================

/**
 * Hook for getting inventory condition options for selectors
 */
export function useInventoryConditionOptions(filters?: DomainStatusFilters) {
  const { options, isLoading, error } = useInventoryConditions({ filters });
  
  return {
    options,
    isLoading,
    error,
  };
}

/**
 * Hook for getting a single inventory condition by ID
 */
export function useInventoryCondition(id: string | undefined) {
  const { user } = useAuth();
  
  return useQuery({
    queryKey: inventoryConditionsKeys.detail(id || ''),
    queryFn: async (): Promise<InventoryCondition | null> => {
      if (!user?.id || !id) {
        return null;
      }

      const { data, error } = await supabaseWorker
        .from('inventory_conditions')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (error) {
        const code = (error as { code?: string } | null)?.code;
        if (code === 'PGRST116') {
          return null; // Not found
        }
        const errMsg = (error as { message?: string } | null)?.message ?? 'Unknown error';
        throw new Error(`Failed to fetch inventory condition: ${errMsg}`);
      }

      return inventoryConditionDbToClient(data as InventoryConditionDb);
    },
    enabled: !!user?.id && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
